import { useCallback, useMemo } from "react";
import { isEmpty } from "lodash";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { StyleMetaAttributes } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import { useCustomColors } from "@circle-react-shared/BuilderUI/ThemeConfiguration/hooks/useCustomColors";
import { Form } from "@circle-react-uikit/Form";
import { useIsPropertiesPanelRevampEnabled } from "../Hooks/useIsPropertiesPanelRevamp";
import { AlignmentField } from "./fields/AlignmentField";
import { BorderRadiusSelector } from "./fields/BorderRadius";
import { BoxModelInput } from "./fields/BoxModelField";
import { FormCustomHtmlEditor } from "./fields/CustomHTMLEditor/FormCustomHtmlEditor";
import { DimensionInput } from "./fields/DimensionInput";
import { EmbedField } from "./fields/EmbedField";
import { FormSelectField } from "./fields/FormSelectField";
import { HeadingLevelPicker } from "./fields/HeadingLevelPicker";
import { ImagePicker } from "./fields/ImagePicker";
import { ListField } from "./fields/ListField";
import { ParagraphLevelPicker } from "./fields/ParagraphLevelPicker";
import { RichTextField } from "./fields/RichTextField";
import { Sizing } from "./fields/Sizing";
import { SliderField } from "./fields/SliderField";
import { UrlField } from "./fields/UrlField";
import { VideoUploader } from "./fields/VideoUploader";
import { VisibilityField } from "./fields/VisibilityField";

export interface EditorFieldProps {
  name: string;
  propMeta: PropsMeta | StyleMetaAttributes;
}

export const DefaultEditorFields = ({ name, propMeta }: EditorFieldProps) => {
  const customEditorFields = useBuilderStore(state => state.customEditorFields);
  const {
    flattenedCssVariables,
    themeColors,
    customColors,
    handleAddCustomColor,
  } = useCustomColors();

  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();

  const label = useMemo(() => {
    const fallbackLabel = propMeta.name;
    const i18nKey = propMeta.nameI18n;
    return i18nKey
      ? t(i18nKey, { defaultValue: fallbackLabel })
      : fallbackLabel;
  }, [propMeta.name, propMeta.nameI18n]);

  const CustomFieldComponent = useMemo(() => {
    if (
      customEditorFields &&
      Object.prototype.hasOwnProperty.call(customEditorFields, propMeta.type)
    ) {
      return customEditorFields[propMeta.type];
    }
    return null;
  }, [customEditorFields, propMeta.type]);

  const getFormItemProps = useCallback(
    ({ className }: { className?: string } = {}) => ({
      hideDescription: true,
      hideBorder: true,
      className: classNames(
        "!p-0",
        {
          "!flex !items-center gap-4": isPropertiesPanelV2,
        },
        className,
      ),
      labelWrapperClassName: classNames("inline-flex", {
        "!w-fit": !isPropertiesPanelV2,
      }),
      labelClassName: "!font-normal text-xs",
    }),
    [isPropertiesPanelV2],
  );

  if (CustomFieldComponent) {
    return <CustomFieldComponent name={name} propMeta={propMeta} />;
  }

  // Custom input fields should be handled by custom editor components.
  // This check here improves the type narrowing of the switch statement below.
  if (propMeta.prop === "custom") {
    return null;
  }

  switch (propMeta.type) {
    case "string":
      return (
        <Form.Item name={name} label={label} {...getFormItemProps()}>
          <Form.Input name={name} />
        </Form.Item>
      );

    case "textarea":
      return (
        <Form.Item name={name} label={label} {...getFormItemProps()}>
          <Form.Input name={name} variant="textarea" rows={7} />
        </Form.Item>
      );

    case "number":
      return (
        <Form.Item name={name} label={label} {...getFormItemProps()}>
          <Form.Input type="number" name={name} {...propMeta.numberProps} />
        </Form.Item>
      );

    case "boolean":
      return (
        <Form.Item
          name={name}
          label={label}
          shouldToggleValueOnLabelClick
          {...getFormItemProps({
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            className: "!flex !items-center",
          })}
        >
          <Form.ToggleSwitch name={name} variant="small" />
        </Form.Item>
      );

    case "circle-image-upload":
    case "background-image":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          labelClassName="!font-normal text-xs"
          className="!p-0"
        >
          <ImagePicker
            name={name}
            shouldAllowRemovingImage={!!propMeta.shouldAllowRemovingImage}
          />
        </Form.Item>
      );

    case "circle-video-upload":
      return <VideoUploader name={name} />;

    case "color":
    case "color-without-alpha":
      return (
        <Form.Item
          name={name}
          label={label}
          {...getFormItemProps({
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            className: "!flex !w-full !items-center !justify-between",
          })}
        >
          <Form.ColorPickerV2
            name={name}
            cssVariables={flattenedCssVariables}
            shouldResolveCssVariable={Boolean(flattenedCssVariables)}
            shouldRenderThemeColors={!isEmpty(themeColors)}
            shouldShowAlpha={propMeta.type === "color"}
            themeColors={themeColors}
            customColors={customColors}
            onAddCustomColor={
              !isEmpty(themeColors) ? handleAddCustomColor : undefined
            }
          />
        </Form.Item>
      );

    case "select": {
      return (
        <Form.Item name={name} label={label} {...getFormItemProps()}>
          <Form.SelectNative
            name={name}
            options={propMeta.options}
            placeholder={label}
          />
        </Form.Item>
      );
    }

    case "custom-html":
      return (
        <Form.Item
          name={name}
          label={label}
          description={t("builder_ui.custom_html.disclaimer_message")}
          hideBorder
          labelClassName="!font-normal text-xs"
          className="!p-0"
        >
          <div className="mt-2" />
          <FormCustomHtmlEditor name={name} />
        </Form.Item>
      );

    case "list":
      return <ListField name={name} />;

    case "oembed":
      return <EmbedField name={name} label={label} />;

    case "slider": {
      return (
        <SliderField
          name={name}
          label={label}
          inputClassName="w-16"
          {...propMeta.sliderProps}
        />
      );
    }

    case "url":
      return <UrlField name={name} label={label} />;

    case "border-radius":
      return <BorderRadiusSelector name={name} />;

    case "alignment":
      return (
        <AlignmentField
          name={name}
          options={propMeta.alignment_options}
          label={label}
        />
      );

    case "dimension":
      return <DimensionInput name={name} label={label} />;

    case "sizing":
      return <Sizing />;

    case "box-model":
      return (
        <BoxModelInput
          name={name}
          label={label}
          type={propMeta.boxModelType ?? "padding"}
        />
      );

    case "heading-level-picker":
      return <HeadingLevelPicker label={label} />;

    case "rich-text":
      return <RichTextField name={name} propMeta={propMeta} />;
    case "paragraph-style-picker":
      return <ParagraphLevelPicker label={label} />;

    case "form-select":
      return <FormSelectField name={name} propMeta={propMeta} />;

    case "visibility":
      return <VisibilityField name={name} label={label} />;

    default:
      return null;
  }
};
